import React from 'react';
import { Document, Page, View } from '@react-pdf/renderer';
import type { PhotoDataUrls } from '../../../utils/pdf';
import { cardSize } from '../catalog';
import type { CardPerson } from '../cardPeople';
import IdCardFace, { type CardSide } from './IdCardFace';
import type { CardFaceProps, CardSchool } from './faces/shared';

/** Card printers take one card per page; office printers take an A4 sheet. */
export type CardPaper = 'card' | 'a4';

interface Props {
  design: CardFaceProps['design'];
  people: CardPerson[];
  photos: PhotoDataUrls;
  school: CardSchool;
  validUntil: string | null;
  paper: CardPaper;
  title: string;
}

const MM = 72 / 25.4;
const A4 = { width: 595.28, height: 841.89 };
/** Space between cards on a sheet, and the least sheet margin — room for
 *  the cutter and the crop marks inside a printer's unprintable edge. */
const GUTTER = 3 * MM;
const MARGIN = 7 * MM;
const MARK = 3.2 * MM;
const MARK_GAP = 1.2 * MM;

/** How many cards fit an A4 sheet: 2 × 5 landscape, 3 × 3 portrait. */
const sheetGrid = (width: number, height: number) => {
  const cols = Math.floor((A4.width - 2 * MARGIN + GUTTER) / (width + GUTTER));
  const rows = Math.floor((A4.height - 2 * MARGIN + GUTTER) / (height + GUTTER));
  return { cols, rows, perSheet: cols * rows };
};

const chunk = <T,>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, i) => items.slice(i * size, i * size + size));

/** Crop marks in the sheet margin, in line with every card edge. */
const CropMarks: React.FC<{ left: number; top: number; cols: number; rows: number; width: number; height: number }> = ({ left, top, cols, rows, width, height }) => {
  const xs = Array.from({ length: cols }, (_, c) => [left + c * (width + GUTTER), left + c * (width + GUTTER) + width]).flat();
  const ys = Array.from({ length: rows }, (_, r) => [top + r * (height + GUTTER), top + r * (height + GUTTER) + height]).flat();
  const right = left + cols * width + (cols - 1) * GUTTER;
  const bottom = top + rows * height + (rows - 1) * GUTTER;
  const line = { position: 'absolute' as const, backgroundColor: '#9CA3AF' };
  return (
    <>
      {xs.map((x, i) => (
        <React.Fragment key={`x${i}`}>
          <View style={{ ...line, left: x - 0.25, top: top - MARK_GAP - MARK, width: 0.5, height: MARK }} />
          <View style={{ ...line, left: x - 0.25, top: bottom + MARK_GAP, width: 0.5, height: MARK }} />
        </React.Fragment>
      ))}
      {ys.map((y, i) => (
        <React.Fragment key={`y${i}`}>
          <View style={{ ...line, top: y - 0.25, left: left - MARK_GAP - MARK, height: 0.5, width: MARK }} />
          <View style={{ ...line, top: y - 0.25, left: right + MARK_GAP, height: 0.5, width: MARK }} />
        </React.Fragment>
      ))}
    </>
  );
};

/**
 * The cards as a PDF. On a card printer every card is a page, its back the
 * next page. On A4 each sheet of fronts is followed by its sheet of backs,
 * columns mirrored, so a long-edge duplex print lines each back up with
 * its front.
 */
const IdCardsDocument: React.FC<Props> = ({ design, people, photos, school, validUntil, paper, title }) => {
  const { width, height } = cardSize(design.layout);
  const sides: CardSide[] = design.back.enabled ? ['front', 'back'] : ['front'];
  const face = (person: CardPerson, side: CardSide) => (
    <IdCardFace side={side} design={design} person={person} photo={photos[person.photoFilename ?? ''] ?? null} school={school} validUntil={validUntil} />
  );

  if (paper === 'card') {
    return (
      <Document title={title} author={school.name}>
        {people.flatMap(p => sides.map(side => (
          <Page key={`${p.key}-${side}`} size={[width, height]} wrap={false}>{face(p, side)}</Page>
        )))}
      </Document>
    );
  }

  const { cols, rows, perSheet } = sheetGrid(width, height);
  const gridWidth = cols * width + (cols - 1) * GUTTER;
  const gridHeight = rows * height + (rows - 1) * GUTTER;
  const left = (A4.width - gridWidth) / 2;
  const top = (A4.height - gridHeight) / 2;

  return (
    <Document title={title} author={school.name}>
      {chunk(people, perSheet).flatMap((sheet, s) => sides.map(side => (
        <Page key={`${s}-${side}`} size="A4" wrap={false}>
          {/* The sheet's full size, in flow: a page of only absolutely
              placed cards would otherwise collapse to no height. */}
          <View style={{ width: A4.width, height: A4.height }}>
            <CropMarks left={left} top={top} cols={cols} rows={rows} width={width} height={height} />
            {sheet.map((p, i) => {
              const row = Math.floor(i / cols);
              const col = side === 'back' ? cols - 1 - (i % cols) : i % cols;
              return (
                <View key={p.key} style={{ position: 'absolute', left: left + col * (width + GUTTER), top: top + row * (height + GUTTER), width, height }}>
                  {face(p, side)}
                </View>
              );
            })}
          </View>
        </Page>
      )))}
    </Document>
  );
};

export default IdCardsDocument;
